import { z } from "zod";

/**
 * Verdict enums for the Ignite focus quiz.
 *
 * Deliberately PARALLEL to the T-suppression profile's enums rather than
 * shared with them: the shapes match (a level, an assessment, two ranked
 * causes, a quality-of-life reading, an outcome) but the vocabularies do not,
 * and the Boost labels live in a `web/src/i18n/` pack consumed by every live
 * quiz (rule 13). Reusing them would restyle twenty surfaces to change one.
 */

/**
 * How much of his attention is being taken. Ascending.
 *
 * `detected` is the softest tier a symptomatic completer can reach. It
 * confirms the problem is present instead of grading it politely -- the same
 * reasoning that removed "NORMAL" and then "MILD" from the Boost verdict
 * (rule 13), applied once here rather than learned twice.
 *
 * `minimal` is reachable ONLY through the all-clear gate in
 * `quiz-focus-profile.ts`: a man who answered clean on all six symptom
 * screens and said it has cost him nothing yet. He counted the evidence
 * himself across those screens, so telling
 * him his attention is being drained is a lie he can detect. Same exemption
 * `cortisol-story` already runs against the rule-12a floor, and it must stay
 * exactly that narrow.
 */
export const ATTENTION_DRAIN_LEVELS = [
  "minimal",
  "detected",
  "moderate",
  "high",
  "severe",
] as const;
export const AttentionDrainLevelSchema = z.enum(ATTENTION_DRAIN_LEVELS);
export type AttentionDrainLevel = z.infer<typeof AttentionDrainLevelSchema>;

/**
 * The four attention drains the analyzing screen promises to match against.
 *
 * Every member is a MECHANISM. The operator's first sketch also offered
 * "Procrastination", "Brain Fog" and "Attention"; all three were cut and must
 * not return:
 *
 * - Procrastination is downstream of lost attention, so naming it the cause is
 *   circular -- and it reads as blame one screen after the interstitial has
 *   just told him it is not a lack of discipline.
 * - Brain fog is a symptom, and "cures brain fog" is on the banned-claims
 *   list, so it cannot carry copy anyway.
 * - "Attention" is not a mechanism at all. "Your primary cause is Attention"
 *   is a category error on the page whose whole job is naming the mechanism.
 *
 * `age-related-decline` is gated on the age answer in `scoreFocusCauses` and
 * scores zero below 45, so it is unreachable for a younger man rather than
 * merely unlikely.
 */
export const FOCUS_CAUSES = [
  "interruption-tax",
  "multitasking-overload",
  "cognitive-depletion",
  "age-related-decline",
] as const;
export const FocusCauseSchema = z.enum(FOCUS_CAUSES);
export type FocusCause = z.infer<typeof FocusCauseSchema>;

/**
 * Reach of the consequence.
 *
 * Shares no member name with `ATTENTION_DRAIN_LEVELS` on purpose: those two
 * enums render as adjacent cells of the same 2x2 grid, and two cells both
 * reading "MODERATE" looks like a template bug rather than a diagnosis.
 * `emerging` rather than `low` for the same no-absolution reason as the drain
 * floor.
 */
export const FOCUS_QOL_LEVELS = [
  "emerging",
  "noticeable",
  "disruptive",
  "pervasive",
] as const;
export const FocusQolSchema = z.enum(FOCUS_QOL_LEVELS);
export type FocusQol = z.infer<typeof FocusQolSchema>;

/**
 * The one optimistic cell on the verdict page, and the bridge into the offer.
 *
 * Every value is flattering by construction, so this can never become a fourth
 * piece of bad news sitting in a grid of three. It MOVES with the answers --
 * a value that is always "HIGH" stops being information the moment anyone
 * compares two screenshots.
 */
export const COGNITIVE_POTENTIALS = ["high", "very-high", "exceptional"] as const;
export const CognitivePotentialSchema = z.enum(COGNITIVE_POTENTIALS);
export type CognitivePotential = z.infer<typeof CognitivePotentialSchema>;

/**
 * One sample of the day chart.
 *
 * `coffee` and `ignite` are normalised 0..1 where 0 is foggy and 1 is sharp.
 * They are a SHAPE, not a measurement -- which is why the rendered chart
 * carries no numbered axis. Focus has no unit, so a scale would invent one.
 */
export const DayPointSchema = z.object({
  hour: z.number().int().min(0).max(23),
  coffee: z.number().min(0).max(1),
  ignite: z.number().min(0).max(1),
});
export type DayPoint = z.infer<typeof DayPointSchema>;

export const FocusProfileSchema = z
  .object({
    drainLevel: AttentionDrainLevelSchema,
    assessment: z.object({
      label: z.string().min(1),
      headline: z.string().min(1),
      explanation: z.string().min(1),
    }),
    /**
     * Primary then secondary, in deriver rank order. Always exactly two, so
     * the breakdown grid can never render an empty cell -- the same no-holes
     * invariant rule 12 enforces on the T-suppression grid.
     */
    causes: z.array(FocusCauseSchema).length(2),
    qualityOfLifeImpact: FocusQolSchema,
    cognitivePotential: CognitivePotentialSchema,
    /**
     * Feeds the post-diagnostic day chart. Derived rather than authored, and
     * carried on the profile rather than computed in the component, so the
     * screen stays reproducible server-side from the persisted `answers` JSON
     * (rule 11).
     */
    dayCurve: z.array(DayPointSchema).min(2),
  })
  .superRefine((profile, ctx) => {
    for (let i = 1; i < profile.dayCurve.length; i++) {
      if (profile.dayCurve[i]!.hour <= profile.dayCurve[i - 1]!.hour) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "dayCurve must be ordered by ascending hour",
          path: ["dayCurve", i, "hour"],
        });
        return;
      }
    }
    if (profile.causes[0] === profile.causes[1]) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "primary and secondary cause must differ",
        path: ["causes"],
      });
    }
  });
export type FocusProfile = z.infer<typeof FocusProfileSchema>;
